export interface SessionScheduleLike {
  id?: number | string;
  date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  location?: string | null;
  is_cancelled?: boolean;
}

export interface SessionLike {
  id?: number | string;
  name?: string;
  start_date?: string | null;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  location?: string | null;
  days_of_week?: string[] | null;
  days_display?: string | null;
  schedules?: SessionScheduleLike[];
}

export interface ProgramLike {
  payment_type?: string;
  is_one_time_payment?: boolean;
  sessions_per_month?: number;
  program_start_date?: string | null;
  program_end_date?: string | null;
  available_sessions?: SessionLike[];
}

export interface ProgramScheduleEntry {
  key: string;
  sessionName: string;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  location: string | null;
}

const parseDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parts = value.split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
};

export const formatScheduleDate = (value?: string | null): string => {
  const parsed = parseDate(value);
  if (!parsed) return 'TBD';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed);
};

export const formatScheduleTime = (value?: string | null): string => {
  if (!value) return 'TBD';
  const [hours, minutes] = value.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return value;
  const parsed = new Date();
  parsed.setHours(hours, minutes, 0, 0);
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
};

const sortEntries = (entries: ProgramScheduleEntry[]) =>
  [...entries].sort((left, right) => {
    const leftDate = parseDate(left.date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const rightDate = parseDate(right.date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    if (leftDate !== rightDate) return leftDate - rightDate;
    return (left.startTime || '').localeCompare(right.startTime || '');
  });

export const getSessionScheduleEntries = (session?: SessionLike | null): ProgramScheduleEntry[] => {
  if (!session) return [];

  const activeSchedules = (session.schedules || []).filter((schedule) => !schedule.is_cancelled);
  if (activeSchedules.length > 0) {
    return sortEntries(
      activeSchedules.map((schedule, index) => ({
        key: `${session.id || session.name || 'session'}-${schedule.id || index}`,
        sessionName: session.name || 'Session',
        date: schedule.date || null,
        startTime: schedule.start_time || null,
        endTime: schedule.end_time || null,
        location: schedule.location || session.location || null,
      }))
    );
  }

  if (!session.start_date && !session.start_time && !session.end_time) return [];

  return [
    {
      key: `${session.id || session.name || 'session'}-default`,
      sessionName: session.name || 'Session',
      date: session.start_date || null,
      startTime: session.start_time || null,
      endTime: session.end_time || null,
      location: session.location || null,
    },
  ];
};

export const getProgramScheduleEntries = (program?: ProgramLike | null): ProgramScheduleEntry[] =>
  sortEntries(
    (program?.available_sessions || []).flatMap((session) => getSessionScheduleEntries(session))
  );

export const getProgramScheduleRange = (program?: ProgramLike | null): string | null => {
  const entries = getProgramScheduleEntries(program);
  if (entries.length > 0) {
    const first = entries[0];
    const last = entries[entries.length - 1];
    const firstLabel = formatScheduleDate(first.date);
    const lastLabel = formatScheduleDate(last.date);
    return firstLabel === lastLabel ? firstLabel : `${firstLabel} to ${lastLabel}`;
  }

  if (program?.program_start_date || program?.program_end_date) {
    const startLabel = formatScheduleDate(program.program_start_date);
    const endLabel = formatScheduleDate(program.program_end_date);
    return startLabel === endLabel ? startLabel : `${startLabel} to ${endLabel}`;
  }

  return null;
};

const WEEKDAY_NAMES = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];

/** Derive the actual weekday name from a real session date (e.g. start_date),
 * used when days_of_week isn't populated on the Session row. */
const weekdayFromDate = (value?: string | null): string | null => {
  const parsed = parseDate(value);
  return parsed ? WEEKDAY_NAMES[parsed.getDay()] : null;
};

export const formatDaysOfWeek = (days?: string[] | string | null): string | null => {
  if (Array.isArray(days) && days.length) {
    return days.map((d) => d.replace(/^\w/, (c) => c.toUpperCase())).join(', ');
  }
  if (typeof days === 'string' && days.trim()) return days;
  return null;
};

export const getProgramScheduleDayTime = (session?: SessionLike | null): string | null => {
  if (!session) return null;
  const days =
    formatDaysOfWeek(session.days_display || session.days_of_week) ||
    weekdayFromDate(session.start_date) ||
    'Schedule TBD';
  const startTime = formatScheduleTime(session.start_time);
  const endTime = formatScheduleTime(session.end_time);
  return `${days}, ${startTime} - ${endTime}`;
};

export const getProgramScheduleSummary = (program?: ProgramLike | null): string => {
  const isOneTime = program?.payment_type === 'one_time' || program?.is_one_time_payment;
  if (isOneTime) {
    return getProgramScheduleRange(program) || 'Schedule announced after registration';
  }

  const sessionCount = program?.available_sessions?.length || program?.sessions_per_month || 4;
  return `${sessionCount} session${sessionCount === 1 ? '' : 's'} included`;
};
